import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

export interface SceneRenderer {
  scene: THREE.Scene;
  start: () => void;
  mount: (container: HTMLElement) => void;
}

export function createSceneRenderer(): SceneRenderer {
  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.1;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xeae5de);
  scene.fog = new THREE.Fog(0xeae5de, 40, 120);

  const camera = new THREE.PerspectiveCamera(30, 1, 0.1, 500);
  camera.position.set(20, 28, 20);
  camera.lookAt(0, 0, 0);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.target.set(0, 0, 2);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.maxPolarAngle = Math.PI / 2.3;
  controls.minDistance = 10;
  controls.maxDistance = 80;
  controls.update();

  scene.add(new THREE.AmbientLight(0xffffff, 0.65));
  scene.add(new THREE.HemisphereLight(0xf0f0f0, 0xd0c8b8, 0.5));

  const dirLight = new THREE.DirectionalLight(0xfff8f0, 1.6);
  dirLight.position.set(15, 30, 10);
  dirLight.castShadow = true;
  dirLight.shadow.mapSize.set(2048, 2048);
  dirLight.shadow.camera.left = -40;
  dirLight.shadow.camera.right = 40;
  dirLight.shadow.camera.top = 40;
  dirLight.shadow.camera.bottom = -40;
  dirLight.shadow.camera.near = 1;
  dirLight.shadow.camera.far = 80;
  dirLight.shadow.bias = -0.001;
  scene.add(dirLight);

  const fillLight = new THREE.DirectionalLight(0xe8e4f0, 0.4);
  fillLight.position.set(-10, 15, -10);
  scene.add(fillLight);

  let container: HTMLElement | null = null;

  function updateSize() {
    if (!container) return;
    const { width, height } = container.getBoundingClientRect();
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height, false);
  }

  window.addEventListener("resize", updateSize);

  function mount(el: HTMLElement) {
    container = el;
    renderer.domElement.style.display = 'block';
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    el.appendChild(renderer.domElement);
    updateSize();
  }

  function start() {
    (function animate() {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    })();
  }

  return { scene, start, mount };
}
